import {
  getOrg,
  getProjectHome,
  type HardwareRevision,
  type Issue,
  type Job,
  type Milestone,
  type OrgRow,
  type ProjectHome,
  type Requirement,
  type Review,
  type Role,
} from "@/lib/api";
import { projectScopeFromHome } from "@/lib/project-scope-home";
import { isScopeLoading, scopeLoadKey } from "@/lib/scope-loading";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";

export type ProjectScope = {
  orgSlug: string | undefined;
  projectSlug: string | undefined;
  org: OrgRow | null;
  project: { id: string; slug: string; name: string } | null;
  projects: { id: string; slug: string; name: string }[];
  home: ProjectHome | null;
  hardware: HardwareRevision[];
  reviews: Review[];
  issues: Issue[];
  jobs: Job[];
  requirements: Requirement[];
  milestones: Milestone[];
  orgBase: string;
  projectBase: string | null;
  role: Role | null;
  loading: boolean;
};

const empty: ProjectScope = {
  orgSlug: undefined,
  projectSlug: undefined,
  org: null,
  project: null,
  projects: [],
  home: null,
  hardware: [],
  reviews: [],
  issues: [],
  jobs: [],
  requirements: [],
  milestones: [],
  orgBase: "",
  projectBase: null,
  role: null,
  loading: false,
};

const Ctx = createContext<ProjectScope>(empty);

export function useProjectScope(): ProjectScope {
  return useContext(Ctx);
}

export function ProjectScopeProvider({ children }: { children: ReactNode }) {
  const { orgSlug, projectSlug } = useParams();
  const [org, setOrg] = useState<OrgRow | null>(null);
  const [projects, setProjects] = useState<{ id: string; slug: string; name: string }[]>([]);
  const [home, setHome] = useState<ProjectHome | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const currentKey = scopeLoadKey(orgSlug, projectSlug);
  const loading = isScopeLoading(orgSlug, loadedKey, currentKey);

  useEffect(() => {
    if (!orgSlug) {
      setOrg(null);
      setProjects([]);
      setHome(null);
      setLoadedKey(scopeLoadKey(orgSlug, projectSlug));
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        if (projectSlug) {
          const h = await getProjectHome(orgSlug, projectSlug);
          if (cancelled) return;
          const scoped = projectScopeFromHome(h);
          setHome(h);
          setOrg(scoped.org);
          setProjects(scoped.projects);
        } else {
          const o = await getOrg(orgSlug);
          if (cancelled) return;
          setHome(null);
          setOrg(o.org);
          setProjects(o.projects ?? []);
        }
      } catch {
        if (!cancelled) {
          setHome(null);
          setOrg(null);
          setProjects([]);
        }
      } finally {
        if (!cancelled) setLoadedKey(scopeLoadKey(orgSlug, projectSlug));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgSlug, projectSlug]);

  const value = useMemo<ProjectScope>(() => {
    const orgBase = orgSlug ? `/o/${orgSlug}` : "";
    const projectBase = orgSlug && projectSlug ? `${orgBase}/p/${projectSlug}` : null;
    const orgForScope = !orgSlug ? null : org?.slug === orgSlug ? org : { slug: orgSlug, name: orgSlug };
    const homeForScope =
      loading || !home
        ? null
        : projectSlug && home.project?.slug && home.project.slug !== projectSlug
          ? null
          : home;
    const project =
      projectSlug == null
        ? null
        : homeForScope?.project ?? { id: "", slug: projectSlug, name: projectSlug };
    return {
      orgSlug,
      projectSlug,
      org: orgForScope,
      project,
      projects: loading ? [] : projects,
      home: homeForScope,
      hardware: homeForScope?.hardware ?? [],
      reviews: homeForScope?.reviews ?? [],
      issues: homeForScope?.issues ?? [],
      jobs: homeForScope?.jobs ?? [],
      requirements: homeForScope?.requirements ?? [],
      milestones: homeForScope?.milestones ?? [],
      orgBase,
      projectBase,
      role: homeForScope?.role ?? null,
      loading,
    };
  }, [orgSlug, projectSlug, org, projects, home, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
